<template lang="pug">
.bq-service-layout(v-if="currentService")
    aside.bq-sidebar
        SideNav

    main.bq-main(@dragover="swallowDrop" @drop="swallowDrop")
        //- banners & warnings (shared across all service pages)
        EmailVerifyBanner

        //- Plan state banners, built in code/plan-copy.ts: one banner per project
        //- state (suspended, disabled, contained, deleted, payment failed, cancel
        //- scheduled), then the over or near limit warning and the bandwidth
        //- banner. Limit rows name what is over and where to delete it.
        template(v-for="b in banners" :key="b.id")
            .bq-banner(:class="'bq-banner--' + b.tone")
                span.material-symbols-outlined.bq-banner-icon(translate="no") {{ b.icon }}
                span.bq-banner-text
                    | {{ b.text }}
                    template(v-for="r in b.rows" :key="r.key")
                        br
                        span(translate="no") {{ r.text }}
                        template(v-if="r.action")
                            |
                            router-link.bq-link(v-if="r.action.to" :to="r.action.to") [{{ r.action.label }}]
                            a.bq-link(v-else :href="r.action.href" target="_blank" rel="noopener") [{{ r.action.label }}]
                        template(v-else-if="r.hint")
                            |  {{ r.hint }}
                    template(v-if="b.after")
                        br
                        | {{ b.after }}
                template(v-for="a in b.actions" :key="a.label")
                    |
                    router-link.bq-link(v-if="a.to" :to="a.to") [{{ a.label }}]
                    a.bq-link(v-else-if="a.href" :href="a.href" target="_blank" rel="noopener") [{{ a.label }}]
                    a.bq-link(v-else @click="runAction(a)") [{{ a.label }}]

        //- EMAIL NOT VERIFIED: the whole page is locked here, not only the controls
        //- each page remembers to gate. Every page used to gate per element, and the
        //- ones nobody gated stayed live (the advanced search, the pagers, the mobile
        //- lists, a form opened by a ?id= link, the drop overlay on Web Hosting). `inert`
        //- takes pointer events, focus, the keyboard and file drops away from everything
        //- inside; the style block dims it and is the fallback for a browser without
        //- inert. The banner above stays live so [Verify now] works, and the settings
        //- page is left out: its cards gate themselves, and its Plan & Usage card is
        //- meant to stay usable (the plan is resumed from there).
        .bq-page(:inert="pageLocked" :class="{ 'bq-page--locked': pageLocked }")
            router-view

    //- Back to top: floats in the bottom-right corner of the viewport once the
    //- page has scrolled past the first screen (showBackToTop), fading in and
    //- out; the jump is instant on purpose. Desktop only: isDesktop follows the
    //- sidebar's 800px breakpoint, so it is not rendered on phones at all.
    //- Rendered at the end of App.vue's content block (#bq-to-top-slot) so the
    //- sticky button below stops above the footer; defer: the slot is rendered
    //- after the router view, so it is looked up once this render is done.
    //- Guarded: without the slot (a page mounted outside App.vue, as in a test
    //- harness) a Teleport with a missing target throws on the next patch.
    Teleport(v-if="toTopSlotReady" to="#bq-to-top-slot" defer)
        Transition(name="bq-to-top")
            button.bq-to-top(v-if="showBackToTop && isDesktop" type="button" title="Back to top" aria-label="Back to top" @click="scrollToTop")
                svg(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                    path(d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z")

//- After a paid checkout for a new project (/my-projects/await-<id>): the billing
//- ticket creates the project once the payment is confirmed, so wait for it here.
.bq-await(v-else-if="awaiting")
    BunnyLoader(v-if="awaiting !== 'gave-up'" label="Setting up your project")
    h1.bq-await-title {{ awaiting === 'gave-up' ? 'Your project is not ready yet' : 'Payment received' }}
    p.bq-await-desc(v-if="awaiting === 'waiting'") Your project is being created. This page opens it as soon as it is ready.
    p.bq-await-desc(v-else-if="awaiting === 'slow'") This is taking longer than usual. You can leave this page: the project appears in your project list once it is ready.
    p.bq-await-desc(v-else) The payment went through, but the project has not appeared yet. It is added to your project list once it is ready. If it does not appear, contact support@broadwayinc.com.
    router-link.bq-cta-link(v-if="awaiting !== 'waiting'" to="/my-projects") Go to your projects

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { user, customer, openBillingPortal } from "@/code/user";
import { skapi } from "@/main";
import { fullScreenLoader, removeFullScreenLoader } from "@/components/jsComponents";
import {
    currentService,
    serviceList,
    pullServices,
    prefetchServiceRecords,
    prefetchServiceUsers,
} from "@/views/project-list";

import SideNav from "@/components/sideNav.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import { clearPendingProject } from "@/views/new-project";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import { projectBanners } from "@/code/plan-copy";
import { loadPlanSpecs } from "@/code/service-spec";
import type { BannerAction } from "@/code/plan-copy";

const router = useRouter();
const route = useRoute();

let serviceId = route.path.split("/")[2];

/**
 * The plan state banners, built in code/plan-copy.ts from `service.active`, the
 * suspension attributes on the row and the Stripe subscription. Driven by the
 * state itself, never by the derived `will_terminate` date, so a suspended
 * project with no date still gets its banner.
 */
const banners = computed(() => (currentService.value ? projectBanners(currentService.value) : []));

/**
 * The page is locked while the signed-in user's email is not verified (see the
 * .bq-page wrapper in the template). The settings page keeps its own gating.
 */
const pageLocked = computed(() => !user?.email_verified && route.name !== "dashboard");

// A file dropped on a locked page lands on this element, since nothing inside takes
// pointer events any more; without this the browser would open the file in the tab.
// Unlocked, the pages' own drop zones handle their drops and this does nothing.
const swallowDrop = (e: DragEvent) => {
    if (pageLocked.value) e.preventDefault();
};

const runAction = async (a: BannerAction) => {
    if (a.run === "billing") return openBillingPage();
    if (a.run === "refresh") {
        try {
            await currentService.value?.refresh();
        } catch (err) {
            console.warn("[main] could not refresh the project", err);
        }
        currentService.value?.applySubscriptionDerived();
    }
};

// ─── waiting for a project a paid checkout is creating ───
// Every 3 s for the first 2 minutes, then every 10 s; after 15 minutes stop
// and say so (the project still lands in the list whenever it is created).
const AWAIT_FAST_MS = 3000;
const AWAIT_SLOW_MS = 10000;
const AWAIT_SLOW_AFTER_MS = 2 * 60 * 1000;
const AWAIT_GIVE_UP_MS = 15 * 60 * 1000;
const awaiting = ref<"" | "waiting" | "slow" | "gave-up">("");
let awaitTimer: ReturnType<typeof setTimeout> | null = null;
let awaitStopped = false;

async function awaitNewProject(id: string) {
    awaiting.value = "waiting";
    removeFullScreenLoader();
    const started = Date.now();
    const poll = async () => {
        if (awaitStopped) return;
        let found: any = null;
        try {
            found = await pullServices({ service_id_to_fetch: id });
            // a list entry can still be loading
            if (found instanceof Promise) found = await found;
        } catch (err) {
            console.warn("[main] could not check for the new project", err);
        }
        if (awaitStopped) return;
        if (found) {
            clearPendingProject();
            awaiting.value = "";
            serviceId = id;
            currentService.value = found;
            // The new path raises the router's full-screen overlay, and on a project
            // route the router leaves removing it to this component, whose run() does
            // not run again for the same component instance: drop it here.
            await router.replace(`/my-projects/${id}`);
            removeFullScreenLoader();
            void prefetchServiceRecords(found);
            void prefetchServiceUsers(found);
            return;
        }
        const elapsed = Date.now() - started;
        if (elapsed >= AWAIT_GIVE_UP_MS) {
            awaiting.value = "gave-up";
            return;
        }
        if (elapsed >= AWAIT_SLOW_AFTER_MS) awaiting.value = "slow";
        awaitTimer = setTimeout(poll, elapsed >= AWAIT_SLOW_AFTER_MS ? AWAIT_SLOW_MS : AWAIT_FAST_MS);
    };
    await poll();
}

onUnmounted(() => {
    awaitStopped = true;
    if (awaitTimer) clearTimeout(awaitTimer);
});

// Back to top (the floating disc in the bottom-right corner). Shown once the
// window has scrolled past BACK_TO_TOP_AT px, hidden again above it. The click
// jumps straight to the top with no animation (owner's call); "instant" pins
// that even if a smooth scroll-behavior is ever added to the page. The scroll
// listener is passive because the handler never cancels the scroll. Desktop
// only: isDesktop tracks the sidebar's breakpoint (the 800px media query in
// the style block), so the button is not in the DOM at phone widths.
const BACK_TO_TOP_AT = 240;
const SIDEBAR_HIDDEN_QUERY = "(max-width: 800px)";
const showBackToTop = ref(false);
// True once App.vue's #bq-to-top-slot is in the document (checked on mount).
const toTopSlotReady = ref(false);
const isDesktop = ref(true);
let sidebarHidden: MediaQueryList | null = null;
const onWindowScroll = () => {
    showBackToTop.value = window.scrollY > BACK_TO_TOP_AT;
};
const onSidebarBreakpoint = () => {
    isDesktop.value = !sidebarHidden?.matches;
};
const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
};
onMounted(() => {
    toTopSlotReady.value = !!document.getElementById("bq-to-top-slot");
    onWindowScroll();
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    sidebarHidden = window.matchMedia(SIDEBAR_HIDDEN_QUERY);
    sidebarHidden.addEventListener("change", onSidebarBreakpoint);
    onSidebarBreakpoint();
});
onUnmounted(() => {
    window.removeEventListener("scroll", onWindowScroll);
    sidebarHidden?.removeEventListener("change", onSidebarBreakpoint);
});

async function run() {
    currentService.value = null;
    // Plan & Usage draws the served limits; a page opened straight on a project
    // has no plan cards to fetch them, so ask here (a no-op once they are in).
    void loadPlanSpecs();

    if (serviceId.startsWith("await-")) {
        return awaitNewProject(serviceId.replace("await-", ""));
    }
    fullScreenLoader();

    let serviceInst = serviceList?.[serviceId];
    if (!serviceInst) {
        serviceInst = pullServices({ service_id_to_fetch: serviceId });
    }

    if (serviceInst instanceof Promise) {
        serviceInst = await serviceInst;
    }

    if (!serviceInst) {
        removeFullScreenLoader();
        router.push('/my-projects');
        return;
    }

    currentService.value = serviceInst;
    removeFullScreenLoader();

    // Warm the tab caches (fire-and-forget, deduped, publish-only-if-empty):
    // Database/Users then paint instantly from their module pagers.
    void prefetchServiceRecords(currentService.value);
    void prefetchServiceUsers(currentService.value);

    // // Subdomain info (only the service views need it): on VISIT, but only ONCE
    // // per service: subdInfo.srvc is set the first time a fetch settles, and the
    // // Service instance is cached across visits, so skip the re-fetch on re-entry.
    // // (The hosting view still re-fetches via getInfo() after an actual subdomain
    // // change; the internal poll's own timer guard covers in-progress fetches.)
    // if (!currentService.value?.subdInfo?.srvc) {
    //     currentService.value?.getSubdomainInfo?.();
    // }
}
run();

// The provider's customer portal; see openBillingPortal in code/user.ts.
let openBillingPage = () => openBillingPortal();
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-await {
    max-width: 36rem;
    margin: 0 auto;
    padding: 5rem 1.5rem;
    text-align: center;
    font-family: @mono;
    color: @ink;
}

.bq-await-title {
    font-size: clamp(1.2rem, 3vw, 1.6rem);
    font-weight: 700;
    margin: 1.5rem 0 0.75rem;
}

.bq-await-desc {
    color: @muted;
    font-size: 0.88rem;
    line-height: 1.6;
    margin: 0 0 1.5rem;
}

.bq-shell-loading {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: @mono;
    color: @ink;
}

.bq-service-layout {
    display: flex;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    background: @paper;
    color: @ink;
    font-family: @mono;
}

.bq-sidebar {
    position: sticky;
    top: calc(4rem + var(--nav-top));
    width: 212px;
    flex-shrink: 0;
    overflow-y: auto;
    align-self: flex-start;
}

/* Back to top: a disc floating in the bottom-right corner of the viewport,
   where people look for it. Paper face, control border, ink chevron and the
   hard offset shadow the cards and menus carry; the border darkens to ink on
   hover while the face stays paper (box buttons never fill on hover). Size,
   face and shape override the global filled button; the lift and press motion
   base.less gives every button stays. Layer: above everything on the page (the
   column menus stop at 1000) and under the toasts (9999); modals are native
   dialogs in the top layer. The media rule below hides it at the sidebar
   breakpoint as well, a fallback for the v-if. */
.bq-to-top {
    // In flow at the very end of the page content (App.vue's #bq-to-top-slot,
    // reached through the Teleport in the template) and sticky to the bottom
    // of the viewport: while the page goes on below, it sits 1.5rem from the
    // corner exactly as a fixed button would; when the end of the content
    // comes into view it rides up with it and stops 1.5rem above the footer,
    // so it never covers the footer links. The negative top margin cancels its
    // own height plus the gap, so the footer does not move down.
    position: sticky;
    bottom: 1.5rem;
    display: block;
    margin: -4rem 1.5rem 1.5rem auto;
    z-index: 2000;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    border: 1px solid @input-line;
    border-radius: 50%;
    background: @paper;
    color: @ink;
    box-shadow: 2px 2px var(--bq-shadow);

    svg {
        width: 1.5rem;
        height: 1.5rem;
        fill: currentColor;
    }

    &:hover {
        border-color: @ink;
    }
}

/* The fade (Vue transition classes, see the template). */
.bq-to-top-enter-active,
.bq-to-top-leave-active {
    transition: opacity 0.18s ease;
}

.bq-to-top-enter-from,
.bq-to-top-leave-to {
    opacity: 0;
}

.bq-main {
    flex: 1;
    padding: 3rem 3rem 3rem;
    min-width: 0;
}

/* The locked page (email not verified, see the template): dimmed once, as a whole.
   `inert` does the locking; pointer-events is the fallback for a browser without it.
   The controls the pages dim on their own (the global .disabled and .nonClickable,
   native :disabled, the table and advanced search covers) are brought back to full
   strength inside, so the page fades evenly instead of twice. */
.bq-page--locked {
    opacity: 0.5;
    pointer-events: none;
    user-select: none;

    :deep(.disabled),
    :deep(.nonClickable),
    :deep([disabled]),
    :deep(.bq-table-disabled thead),
    :deep(.bq-adv-search--disabled) {
        opacity: 1 !important;
    }
}

/* The banners here are the shared .bq-banner from bq-shared.less: same layout,
   type and icon size as the news banner on the project list. */
.bq-link {
    &:hover {
        text-decoration: underline;
    }
}

@media (max-width: 800px) {
    .bq-sidebar,
    .bq-to-top {
        display: none;
    }

    .bq-main {
        padding: 1rem;
    }
}
</style>
